import React from "react";
import LazyLoad from "react-lazyload";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import Tooltip from "@directv-digital/fusion.molecules.tooltip";
import { KeyValuePair } from "@directv-digital/dds.services.channel-lineup-provider";
import { ChannelCardV2 } from "lib/helper/channel-interactive-tool-v2";
import { IChnlInteractiveThemeUI } from "lib/helper/channel-interactive-tool-theme";
import { iconBannerStyles } from "../../styles/component-styles";
import { getChannelIconOuterBoxStyles, getChannelIconInnerBoxStyles, channelIconMediaStyles, channelIconTooltipPopperProps, channelIconTooltipStyles } from "./styles";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";

interface ChannelIconProps {
  channel: ChannelCardV2;
  category: KeyValuePair;
  index: number;
  theme?: IChnlInteractiveThemeUI;
  isClickable: boolean;
  genreEnabled: boolean;
  onClick: (channel: ChannelCardV2) => void;
}

const ChannelIcon: React.FC<ChannelIconProps> = ({
  channel,
  category,
  index,
  theme,
  isClickable,
  genreEnabled,
  onClick
}) => {
  const handleMouseEnter = (e: React.MouseEvent<HTMLImageElement>) => {
    e.currentTarget.style.transform = isClickable ? "scale(1.1)" : "scale(1)";
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLImageElement>) => {
    e.currentTarget.style.transform = "scale(1)";
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (channel?.label) {
      e.currentTarget.src = "https://www.directv.com/dtvassets/global/component-assets/guide/EmptySVG.svg";
    } else if (e.currentTarget.parentElement) {
      e.currentTarget.parentElement.style.display = "none";
    }
  };

  const handleClick = () => {
    if (isClickable) {
      onClick(channel);
    }
  };

  if (!channel?.label) {
    return null;
  }

  return (
    <DtvBox component="div" key={`${channel.id}-${index}`} sx={{
 ...getChannelIconOuterBoxStyles(theme, genreEnabled, isClickable),
position: "relative"
}}>
      <Tooltip
        describeChild
        PopperProps={channelIconTooltipPopperProps}
        closeOnScroll={true}
        content={channel.label}
        enterTouchDelay={0}
        key={index}
        placement={channel.isLocalChannel ? "top" : "bottom"}
        sx={channelIconTooltipStyles}
        tooltipStyles={iconBannerStyles.tooltipStyles}
      >
        {/* overflow={true} → react-lazyload auto-detects the nearest scrollable ancestor
            (#channels-container, overflow-y:scroll) and attaches its scroll listener there.
            height={112} renders a <div style={{height:"112px"}}> placeholder when the icon
            is not yet visible — critical fix: without a placeholder height every unloaded icon
            collapses to 0px, all 300+ appear at position 0, and react-lazyload loads them all
            at once. offset={100} pre-fetches 100px before the icon scrolls into view. */}
        {channel?.logo && (
          <LazyLoad once height={112} offset={100} overflow={true}>
            <DtvBox
              aria-label={`${channel.label} icon`}
              component="a"
              id={`icon-container-${category.key}-${index}`}
              role="button"
              sx={getChannelIconInnerBoxStyles(isClickable)}
              tabIndex={0}
            >
              {/* Gray circle placeholder — shows while image loads */}
              <DtvBox
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '72%',
                  height: '72%',
                  backgroundColor: '#747474',
                  borderRadius: '50%',
                  zIndex: 0
                }}
              />
              <CardMedia
                alt={channel.label || "channel logo icon"}
                aria-labelledby={`icon-container-${category.key}-${index}`}
                component={"img"}
                height="100px"
                src={channel.logo || "https://www.directv.com/dtvassets/rebrand/global/logos/dtv-logo.svg"}
                width="100px"
                {...(isClickable ? getAnalyticsAttrs(channel.label?.toLowerCase(), 'panel_channel lineup', `/modal_channel_${channel.label}`) : {})}
                sx={{
                  ...channelIconMediaStyles,
                  position: 'relative',
                  zIndex: 1
                }}
                onClick={handleClick}
                onError={handleError}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
            </DtvBox>
          </LazyLoad>
        )}
      </Tooltip>
      {/* Generated by Copilot- Start - 2026-03-18 20:52:58 */}
      {channel.isLocalChannel && (() => {
        const isRsn = channel.rawData?.category === "RSN";
        const badgeLabel = isRsn ? "RSN ⓘ" : "Local ⓘ";
        const badgeWidth = isRsn ? "43px" : "53px";
        const tooltipContent = isRsn ?
          "Regional Sports Networks aren't available everywhere and may vary by package and location. You may need to be in your billing region (ZIP Code) to watch them." :
          "Local channels aren't available everywhere and may vary by location. You may need to be in your billing region (ZIP Code) to watch them.";

        return (
          <DtvBox
            component="span"
            sx={{
              textAlign: "center",
              width: "auto",
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1,
              bottom: "0%"
            }}
          >
            <Tooltip
              describeChild
              closeOnScroll={true}
              content={tooltipContent}
              enterTouchDelay={0}
              leaveTouchDelay={3000}
              placement="bottom"
              PopperProps={{
                sx: {
                  "& .MuiTypography-root": {
                    fontFamily: "roboto-regular",
                    fontSize: "14px"
                  }
                }
              }}
              tooltipStyles={{
                backgroundColor: `#ffffff !important`,
                color: `#111111 !important`
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  cursor: "pointer",
                  color: "#ffff",
                  backgroundColor: "rgb(85, 85, 85)",
                  borderRadius: "4px",
                  letterSpacing: "-0.02em",
                  padding: "2px",
                  margin: "0px auto",
                  fontSize: "12px !important",
                  width: badgeWidth,
                  whiteSpace: "nowrap"
                }}
              >
                {badgeLabel}
              </Typography>
            </Tooltip>
          </DtvBox>
        );
      })()}
      {/* Generated by Copilot- End - 2026-03-18 20:52:58 */}
    </DtvBox>
  );
};

// Memoize to prevent unnecessary re-renders when props haven't changed
// This is critical since ChannelIcon is rendered 100+ times
export default React.memo(ChannelIcon);
